import {
  FloatingOverlay,
  FloatingTree,
  FloatingNode,
  FloatingFocusManager,
  useFloatingNodeId,
  useFloatingParentNodeId,
  FloatingPortal,
  autoUpdate,
  flip,
  offset as floatingOffset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole
} from '@floating-ui/react'
import {
  CSSProperties,
  MouseEvent,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  useState
} from 'react'

type Offset = {
  dx?: number
  dy?: number
}

type PopoverMenuProps = {
  trigger: ReactNode
  children: ReactNode
  placement?: 'bottom-start' | 'bottom-end' | 'bottom' | 'top-start' | 'top-end' | 'top'
  strategy?: 'absolute' | 'fixed'
  isControlled?: boolean
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  offset?: Offset
  overlayClassName?: string
  overlayStyle?: CSSProperties
  overlayLockScroll?: boolean
  floatingClassName?: string
  matchTriggerWidth?: boolean
}

export function PopoverMenu(props: PopoverMenuProps) {
  const parentId = useFloatingParentNodeId()
  if (parentId === null) {
    return (
      <FloatingTree>
        <PopoverMenuContent {...props} />
      </FloatingTree>
    )
  }
  return <PopoverMenuContent {...props} />
}

function PopoverMenuContent({
  trigger,
  children,
  placement = 'bottom-end',
  strategy = 'absolute',
  offset = { dx: 0, dy: 0 },
  isControlled = false,
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange,
  overlayClassName = 'z-20 cursor-default bg-transparent',
  overlayStyle,
  overlayLockScroll = false,
  floatingClassName,
  matchTriggerWidth = false
}: PopoverMenuProps) {
  const nodeId = useFloatingNodeId()
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false)

  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen
  const onOpenChange = isControlled ? controlledOnOpenChange : setUncontrolledIsOpen

  const { refs, floatingStyles, context } = useFloating({
    nodeId,
    open: isOpen,
    onOpenChange,
    placement,
    strategy,
    middleware: [
      floatingOffset({ mainAxis: offset.dy, crossAxis: offset.dx }),
      flip(),
      shift(),
      size({
        padding: 16,
        apply({ availableHeight, rects, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.max(0, availableHeight)}px`,
            width: matchTriggerWidth ? `${rects.reference.width}px` : undefined
          })
        }
      })
    ],
    whileElementsMounted: autoUpdate
  })

  const click = useClick(context)
  const role = useRole(context, { role: 'dialog' })

  const dismiss = useDismiss(context, {
    // Use the same event as Modal: removing a portal on pointerdown can retarget
    // the following mousedown to BODY. The tree keeps that press in one layer.
    outsidePressEvent: 'pointerdown',
    bubbles: { outsidePress: false, escapeKey: false }
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

  return (
    <FloatingNode id={nodeId}>
      {isValidElement(trigger) ? (
        cloneElement(trigger as ReactElement<any>, {
          ref: refs.setReference,
          ...getReferenceProps({
            onClick: (e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation()
            }
          })
        })
      ) : (
        <div ref={refs.setReference} {...getReferenceProps()}>
          {trigger}
        </div>
      )}

      {isOpen && (
        <FloatingPortal>
          <FloatingOverlay
            lockScroll={overlayLockScroll}
            className={overlayClassName}
            style={overlayStyle}
          >
            <FloatingFocusManager
              context={context}
              modal={false}
              initialFocus={-1}
              closeOnFocusOut={false}
            >
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                className={floatingClassName}
                {...getFloatingProps({
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation()
                  }
                })}
              >
                {children}
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </FloatingNode>
  )
}
